/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useMemo } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/PriceRange/PriceRange.css';
import { Price } from '@adobe-commerce/elsie/components/Price';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface PriceRangeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  locale?: string;
  currency?: string;
  amount?: number; // simple product
  variant?: 'default' | 'strikethrough';
  minimumAmount?: number; // configurable product
  maximumAmount?: number; // configurable product
  display?: 'dash' | 'from to' | 'as low as';
  size?: 'small' | 'medium' | 'large';
  specialPrice?: number;
  sale?: boolean;
}

export const PriceRange: FunctionComponent<PriceRangeProps> = ({
  className,
  children,
  locale,
  currency,
  amount,
  variant = 'default',
  minimumAmount,
  maximumAmount,
  size = 'small',
  display = 'dash',
  specialPrice,
  sale = false,
  ...props
}) => {
  const isSimpleProduct = useMemo(
    () =>
      amount ||
      minimumAmount === maximumAmount ||
      (minimumAmount && !maximumAmount) ||
      (maximumAmount && !minimumAmount),
    [amount, maximumAmount, minimumAmount]
  );

  return (
    <div>
      {/* Simple product price or when configurable product minimum price equals to maximum price */}
      {isSimpleProduct ? (
        <div {...props} className={classes(['dropin-price-range', className])}>
          <Price
            amount={amount ?? minimumAmount ?? maximumAmount}
            currency={currency}
            locale={locale}
            size={size}
            variant={variant}
            sale={sale}
          />
        </div>
      ) : (
        <div {...props} className={classes(['dropin-price-range', className])}>
          {display === 'dash' ? (
            <Dashed
              specialPrice={specialPrice}
              minimumAmount={minimumAmount}
              maximumAmount={maximumAmount}
              currency={currency}
              locale={locale}
              size={size}
              sale={sale}
            />
          ) : null}

          {display === 'from to' ? (
            <FromTo
              specialPrice={specialPrice}
              minimumAmount={minimumAmount}
              maximumAmount={maximumAmount}
              currency={currency}
              locale={locale}
              size={size}
              sale={sale}
            />
          ) : null}

          {display === 'as low as' ? (
            <SpecialPrice
              specialPrice={specialPrice}
              minimumAmount={minimumAmount}
              maximumAmount={maximumAmount}
              currency={currency}
              locale={locale}
              size={size}
              sale={sale}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

// Variations

function Dashed({
  specialPrice,
  minimumAmount,
  maximumAmount,
  currency,
  locale,
  size,
  sale,
}: PriceRangeProps) {
  return (
    <>
      <Price
        amount={specialPrice ?? minimumAmount}
        currency={currency}
        locale={locale}
        size={size}
        sale={!!specialPrice && sale}
      />
      <span className="dropin-price-range__separator">-</span>
      <Price
        amount={maximumAmount}
        currency={currency}
        locale={locale}
        size={size}
      />
    </>
  );
}

function FromTo({
  specialPrice,
  minimumAmount,
  maximumAmount,
  currency,
  locale,
  size,
  sale,
}: PriceRangeProps) {
  // Translations
  const translations = useText({
    from: 'Dropin.PriceRange.from.label',
    to: 'Dropin.PriceRange.to.label',
    asLowAs: 'Dropin.PriceRange.asLowAs.label',
  });

  return (
    <>
      <span
        className={classes([
          'dropin-price-range__from',
          `dropin-price-range__from--${size}`,
        ])}
      >
        {translations.from}
      </span>
      <Price
        amount={specialPrice ?? minimumAmount}
        currency={currency}
        locale={locale}
        size={size}
        sale={!!specialPrice && sale}
      />
      <span
        className={classes([
          'dropin-price-range__to',
          `dropin-price-range__to--${size}`,
        ])}
      >
        {translations.to}
      </span>
      <Price
        amount={maximumAmount}
        currency={currency}
        locale={locale}
        size={size}
      />
    </>
  );
}

function SpecialPrice({
  specialPrice,
  minimumAmount,
  maximumAmount,
  currency,
  locale,
  size,
  sale,
}: PriceRangeProps) {
  // Translations
  const translations = useText({
    from: 'Dropin.PriceRange.from.label',
    to: 'Dropin.PriceRange.to.label',
    asLowAs: 'Dropin.PriceRange.asLowAs.label',
  });

  return (
    <>
      <span
        className={classes([
          'dropin-price-range__as-low-as',
          `dropin-price-range__as-low-as--${size}`,
        ])}
      >
        {translations.asLowAs}
      </span>
      {specialPrice ? (
        <div>
          <Price
            amount={maximumAmount}
            currency={currency}
            locale={locale}
            size={size}
            variant="strikethrough"
          />
          <Price
            amount={specialPrice}
            currency={currency}
            locale={locale}
            size={size}
            className="dropin-price-range__special"
            sale={!!specialPrice && sale}
          />
        </div>
      ) : (
        <Price
          amount={minimumAmount}
          currency={currency}
          locale={locale}
          size={size}
        />
      )}
    </>
  );
}
